/**
 * The integrity registry — three independent lanes, never merged into a score
 * (MathToolImplementation.md §6.2; Architecture_V2.01.md §7; ArchitectureV2.lean §6).
 *
 * The three Machine-lane states (`verified` / `unverified` / `conflict-proven`)
 * are kept on a separate track from the Expert lane (ACM-style tiers live there)
 * and the Community lane (flags). The honest defaults and the symmetric burden of
 * proof are realized in the *data*:
 *
 *   - INV-LANE-2 — absence of a re-derivation yields the neutral `unverified`,
 *     NEVER `conflict-proven`. *Lack of proof is never condemnation.*
 *   - INV-LANE-3 — `conflict-proven` requires a *proof* of contradiction under the
 *     *same* config; it is recorded together with a same-config refutation
 *     reference (`refutation_relative`).
 *   - INV-LANE-1 — at most one of `verified` / `conflict-proven` holds for a given
 *     config (the two are distinct constructors; `lane` never returns both).
 *
 * `lane(evidence)` is a 1:1 port of the proved Lean `lane` function
 * (ArchitectureV2.lean §6): a total function from a re-derivation outcome to a
 * Machine-lane state, with the inconsistent / inconclusive inputs refused down to
 * the neutral default.
 */
import { messageId } from '@progression/contracts';
// --- the honest default lane status (INV-LANE-2) -------------------------------
export const DEFAULT_LANES = Object.freeze({
    machine: 'unverified',
    expert: 'not-reviewed',
    community: 'none',
});
// --- the full vocabularies (for L3/L5 to enumerate available actions) ----------
export const MACHINE_LANES = Object.freeze([
    'verified',
    'unverified',
    'conflict-proven',
]);
export const EXPERT_LANES = Object.freeze([
    'not-reviewed',
    'in-progress',
    'audited',
]);
export const COMMUNITY_LANES = Object.freeze(['none', 'flags-exist']);
/**
 * The honest lane assignment (Lean `lane`). Defaults to `unverified`; only a
 * successful re-derivation that establishes `¬P` under the same config gives
 * `conflict-proven`; a clean `P` with no contradiction gives `verified`.
 * Inconsistent input (`P` held AND `¬P` proved) and inconclusive re-runs are
 * refused down to `unverified` — the platform never fabricates a condemnation.
 */
export function lane(e) {
    if (!e.rederived)
        return 'unverified'; // INV-LANE-2: no re-derivation ⇒ unverified
    if (!e.held && e.refutedProof)
        return 'conflict-proven'; // ¬P proved, P did not hold
    if (e.held && !e.refutedProof)
        return 'verified'; // P held, no ¬P proof
    // (held && refutedProof) inconsistent input ⇒ refuse; (!held && !refutedProof) inconclusive.
    return 'unverified';
}
// --- the per-layer seed (one System Claim per layer, honest defaults) ----------
/**
 * Phase 1 seeds one System Claim per layer, each starting at the honest default
 * Machine status until its check re-derives it (Architecture_V2.01.md §7). These
 * are the registry's initial contents; the integrity stream then pushes status
 * records downward into them via `lane-status-recorded` events.
 */
export const SEEDED_SYSTEM_CLAIMS = Object.freeze([
    {
        id: 'sysclaim:L1-conforms',
        descriptionId: messageId('sysclaim.l1-conforms-to-lean'),
        layer: 'L1',
        lanes: DEFAULT_LANES,
    },
    {
        id: 'sysclaim:L2-replays-deterministically',
        descriptionId: messageId('sysclaim.l2-replays-deterministically'),
        layer: 'L2',
        lanes: DEFAULT_LANES,
    },
    {
        id: 'sysclaim:L3-contract-stable',
        descriptionId: messageId('sysclaim.l3-contract-stable'),
        layer: 'L3',
        lanes: DEFAULT_LANES,
    },
    {
        id: 'sysclaim:L4-renders-view-model',
        descriptionId: messageId('sysclaim.l4-renders-view-model'),
        layer: 'L4',
        lanes: DEFAULT_LANES,
    },
    {
        id: 'sysclaim:L5-stable-ids',
        descriptionId: messageId('sysclaim.l5-resolves-stable-ids'),
        layer: 'L5',
        lanes: DEFAULT_LANES,
    },
    {
        id: 'sysclaim:L6-host-isolation',
        descriptionId: messageId('sysclaim.l6-host-reports-isolation-tier'),
        layer: 'L6',
        lanes: DEFAULT_LANES,
    },
]);
// --- Expert lane transition policy (Phase 3; Residual #4 left this to L2/L3) ----
/**
 * The Expert lane carries the ACM review tiers. Transition policy (finalized):
 *   - between two DISTINCT tiers every transition is permitted — a review may
 *     start (`not-reviewed → in-progress`), complete (`→ audited`), be
 *     fast-tracked, re-opened, or downgraded. The tiers are status labels, not a
 *     one-way gated pipeline, and the event log is the authoritative audit trail,
 *     so permitting any move loses no information;
 *   - a self-transition (`X → X`) is an idempotent no-op, so it never churns the
 *     registry or the audit trail.
 * `EXPERT_TRANSITIONS[from]` is the set of permitted *distinct* targets from
 * `from`, so L3/L5 can render exactly these as the available review actions.
 */
export const EXPERT_TRANSITIONS = Object.freeze({
    'not-reviewed': Object.freeze(['in-progress', 'audited']),
    'in-progress': Object.freeze(['audited', 'not-reviewed']),
    audited: Object.freeze(['in-progress', 'not-reviewed']),
});
/** True iff moving the Expert lane `from → to` is permitted (a self-move is allowed). */
export function expertTransitionAllowed(from, to) {
    return from === to || EXPERT_TRANSITIONS[from].includes(to);
}
function isLaneEvent(e) {
    return e.t === 'lane-status-recorded' || e.t === 'flag-raised' || e.t === 'flag-cleared';
}
/**
 * The audit trail for one System Claim: every lane-affecting event targeting it,
 * in log order. Derived purely from the authoritative event log, so the trail is
 * itself event-sourced and replay-stable — the data behind "every transition is
 * an event" (Deliverable A) for Agent 4's queries and Agent 5's display.
 */
export function laneAuditTrail(history, systemClaimId) {
    return Object.freeze(history.filter((e) => isLaneEvent(e) && e.systemClaimId === systemClaimId));
}
//# sourceMappingURL=integrity.js.map